import { ChangeEvent, useCallback, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { validateDirectoryName } from '@components/FileBrowser/utils/utils';

interface ICreateDirectoryDialogProps {
  handleCreate: (d: string) => void;
  handleClose: () => void;
  location: string;
}

const CreateDirectoryDialog = ({
  handleCreate,
  handleClose,
  location,
}: ICreateDirectoryDialogProps) => {
  const [directoryName, setDirectoryName] = useState('');
  const [error, setError] = useState('');

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setDirectoryName(e.target.value);
    },
    [setDirectoryName],
  );

  const handleClickCreate = useCallback(() => {
    if (!directoryName) {
      setError('Directory name cannot be empty');
      return;
    }

    if (!validateDirectoryName(directoryName)) {
      setError('Invalid directory name');
      return;
    }

    handleCreate(directoryName);
    handleClose();
  }, [handleCreate, directoryName, handleClose]);

  return (
    // eslint-disable-next-line react/jsx-boolean-value
    <Dialog onClose={handleClose} open={true} fullWidth maxWidth="xs">
      <DialogTitle>Create New Directory</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Create a directory in {location}
          </Typography>
          <TextField
            label="Directory name"
            onChange={handleChange}
            variant="outlined"
            autoFocus
            fullWidth
          />
        </Stack>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleClickCreate} variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDirectoryDialog;
